import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form, Input, Spin } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import type { OrganizationNameMatch } from '../../types'
import {
  checkOrganizationNameAvailable,
  searchOrganizationNames,
} from '../../api/organization'

interface OrganizationNameFieldProps {
  onDuplicateChange?: (duplicate: boolean) => void
  onCheckingChange?: (checking: boolean) => void
}

const OrganizationNameField: React.FC<OrganizationNameFieldProps> = ({
  onDuplicateChange,
  onCheckingChange,
}) => {
  const form = Form.useFormInstance()
  const companyName = Form.useWatch('companyName', form)
  const [similarOrgs, setSimilarOrgs] = useState<OrganizationNameMatch[]>([])
  const [checkingName, setCheckingName] = useState(false)
  const [duplicateName, setDuplicateName] = useState(false)
  const [panelDismissed, setPanelDismissed] = useState(false)
  const [nameFocused, setNameFocused] = useState(false)
  const [panelMounted, setPanelMounted] = useState(false)
  const [panelVisible, setPanelVisible] = useState(false)
  const searchTimerRef = useRef<number | null>(null)
  const panelTimerRef = useRef<number | null>(null)
  const panelFrameRef = useRef<number | null>(null)
  const requestSeqRef = useRef(0)

  useEffect(() => {
    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current)
    }

    const query = companyName?.trim() || ''
    setPanelDismissed(false)

    if (query.length < 2) {
      requestSeqRef.current += 1
      setSimilarOrgs([])
      setDuplicateName(false)
      setCheckingName(false)
      onDuplicateChange?.(false)
      onCheckingChange?.(false)
      return undefined
    }

    searchTimerRef.current = window.setTimeout(() => {
      const requestSeq = requestSeqRef.current + 1
      requestSeqRef.current = requestSeq
      setCheckingName(true)
      onCheckingChange?.(true)

      Promise.all([
        searchOrganizationNames(query).catch(() => []),
        checkOrganizationNameAvailable(query).catch(() => true),
      ]).then(([matches, available]) => {
        if (requestSeq !== requestSeqRef.current) return
        const hasExactMatch = matches.some(
          (org) => org.name.trim().toLowerCase() === query.toLowerCase(),
        )
        const isDuplicate = !available || hasExactMatch
        setSimilarOrgs(matches)
        setDuplicateName(isDuplicate)
        onDuplicateChange?.(isDuplicate)
      }).finally(() => {
        if (requestSeq !== requestSeqRef.current) return
        setCheckingName(false)
        onCheckingChange?.(false)
      })
    }, 300)

    return () => {
      if (searchTimerRef.current) {
        window.clearTimeout(searchTimerRef.current)
      }
    }
  }, [companyName, onCheckingChange, onDuplicateChange])

  useEffect(() => () => {
    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current)
    }
    if (panelTimerRef.current) {
      window.clearTimeout(panelTimerRef.current)
    }
    if (panelFrameRef.current) {
      window.cancelAnimationFrame(panelFrameRef.current)
    }
  }, [])

  const existingNameInfo = useMemo(() => {
    const currentName = companyName?.trim()
    if (!currentName || similarOrgs.length === 0) {
      return { hasExactMatch: false, visibleOrgs: [] }
    }
    const exactMatch = similarOrgs.some(
      (org) => org.name.trim().toLowerCase() === currentName.toLowerCase(),
    )
    return {
      hasExactMatch: exactMatch,
      visibleOrgs: similarOrgs.slice(0, 8),
    }
  }, [companyName, similarOrgs])

  const shouldRenderExistingNames = !panelDismissed && existingNameInfo.visibleOrgs.length > 0
  const showExistingNames = nameFocused && shouldRenderExistingNames

  useEffect(() => {
    if (panelTimerRef.current) {
      window.clearTimeout(panelTimerRef.current)
      panelTimerRef.current = null
    }
    if (panelFrameRef.current) {
      window.cancelAnimationFrame(panelFrameRef.current)
      panelFrameRef.current = null
    }

    if (showExistingNames) {
      setPanelMounted(true)
      panelFrameRef.current = window.requestAnimationFrame(() => {
        setPanelVisible(true)
        panelFrameRef.current = null
      })
      return undefined
    }

    setPanelVisible(false)
    panelTimerRef.current = window.setTimeout(() => {
      setPanelMounted(false)
      panelTimerRef.current = null
    }, 190)

    return undefined
  }, [showExistingNames])

  return (
    <>
      <Form.Item
        name="companyName"
        label="Organization name"
        className="um-new-org__field"
        validateDebounce={500}
        rules={[
          { required: true, message: 'Organization name is required' },
          {
            validator: async (_, value) => {
              const trimmed = value?.trim()
              if (!trimmed) return Promise.resolve()
              const available = await checkOrganizationNameAvailable(trimmed)
              if (!available) {
                setDuplicateName(true)
                onDuplicateChange?.(true)
                return Promise.reject(
                  new Error('This organization name is already registered for this email address'),
                )
              }
              return Promise.resolve()
            },
          },
        ]}
      >
        <Input
          size="large"
          className="um-new-org__name-input"
          suffix={
            <span className="um-new-org__name-spinner-slot">
              <Spin size="small" className={checkingName ? '' : 'um-new-org__name-spinner--hidden'} />
            </span>
          }
          placeholder="Enter organization name"
          onFocus={() => setNameFocused(true)}
          onBlur={() => setNameFocused(false)}
        />
      </Form.Item>
      {panelMounted && (
        <div
          className={`um-new-org__existing-panel${panelVisible ? ' um-new-org__existing-panel--visible' : ''}`}
          aria-hidden={!panelVisible}
          aria-live="polite"
        >
          <div className="um-new-org__existing-head">
            <div className="um-new-org__existing-title">
              {duplicateName || existingNameInfo.hasExactMatch
                ? 'This organization name already exists. Use a different name.'
                : 'Similar organizations already exist. Please avoid creating duplicates.'}
            </div>
            <Button
              type="text"
              size="small"
              aria-label="Hide existing organizations"
              className="um-new-org__existing-close"
              icon={<CloseOutlined rev={undefined} />}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => setPanelDismissed(true)}
            />
          </div>
          <div className="um-new-org__existing-list">
            {existingNameInfo.visibleOrgs.map((org) => {
              const isExact = org.name.trim().toLowerCase() === companyName?.trim().toLowerCase()
              return (
                <div
                  key={org.id}
                  className={`um-new-org__existing-item${isExact ? ' um-new-org__existing-item--exact' : ''}`}
                  aria-disabled="true"
                >
                  <span className="um-new-org__existing-name">{org.name}</span>
                  <span className="um-new-org__existing-meta">
                    {isExact ? 'Already exists' : 'Existing organization'}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}

export default OrganizationNameField
